<template>
  <div class="sidebar-wrapper bg-white dark:bg-slate-900">
    <logo-long :class="{'fixed-logo': !primaryMenuWithTitles && !hasSecondaryMenu}"
               :source="logo"
               :name="globalConfig.installationName"
               :account-id="accountId"
    />
    <a class="toggle--menu-type" @click="togglePrimaryMenuWithTitles">{{primaryMenuWithTitles ? "→||": "|←"}}</a>
    <aside class="woot-sidebar">
      <primary-sidebar
        :logo-source="logo"
        :installation-name="globalConfig.installationName"
        :is-a-custom-branded-instance="isACustomBrandedInstance"
        :account-id="accountId"
        :menu-items="primaryMenuItems"
        :active-menu-item="activePrimaryMenu.key"
        :with-titles="primaryMenuWithTitles"
        @toggle-accounts="toggleAccountModal"
        @key-shortcut-modal="toggleKeyShortcutModal"
        @open-notification-panel="openNotificationPanel"
      >
        <secondary-sidebar
          v-if="primaryMenuWithTitles && showSecondarySidebar"
          :class="sidebarClassName"
          :account-id="accountId"
          :inboxes="inboxes"
          :labels="labels"
          :teams="teams"
          :custom-views="customViews"
          :menu-config="activeSecondaryMenu"
          :current-role="currentRole"
          :features="features"
          :is-on-chatwoot-cloud="isOnChatwootCloud"
          :show-export-wizard="showExportWizard"
          :show-import-wizard="showImportWizard"
          @add-label="showAddLabelPopup"
          @toggle-accounts="toggleAccountModal"
        />
      </primary-sidebar>
      <secondary-sidebar
        v-if="!primaryMenuWithTitles && showSecondarySidebar"
        :class="sidebarClassName"
        :account-id="accountId"
        :inboxes="inboxes"
        :labels="labels"
        :teams="teams"
        :custom-views="customViews"
        :menu-config="activeSecondaryMenu"
        :current-role="currentRole"
        :features="features"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        :show-export-wizard="showExportWizard"
        :show-import-wizard="showImportWizard"
        @add-label="showAddLabelPopup"
        @toggle-accounts="toggleAccountModal"
      />
    </aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import LogoLong from './sidebarComponents/LogoLong'

import adminMixin from '../../mixins/isAdmin';
import { getSidebarItems } from './config/default-sidebar';
import alertMixin from 'shared/mixins/alertMixin';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import {
  hasPressedAltAndCKey,
  hasPressedAltAndRKey,
  hasPressedAltAndSKey,
  hasPressedAltAndVKey,
  hasPressedCommandAndForwardSlash,
  isEscape,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import router from '../../routes';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

export default {
  components: {
    LogoLong,
    PrimarySidebar,
    SecondarySidebar,
  },
  mixins: [adminMixin, alertMixin, eventListenerMixins, hasPermissionMixin],
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
      getAccount: 'accounts/getAccount',
      features: 'getCurrentFeatures', // d99d
      routePermissions: 'getRoutePermissions', // d99d
    }),
    primaryMenuWithTitles() {
      return this.$store.state.uiSettings.primaryMenuWithTitles;
    },
    account() {
      return this.getAccount(this.accountId);
    },
    logo() {
      return this.account === undefined
        ? undefined
        : this.account.logo_url ||
            this.currentUser.logo_user_url ||
            this.globalConfig.logo;
    },
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    iPracticomFeatures() {
      return this.account.platform_features?.iPracticom;
    },
    primaryMenuItems() {
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        if (
          this.iPracticomFeatures &&
          ['changelog', 'help'].includes(menuItem.key)
        ) {
          return false;
        }
        const isAvailableForTheUser = menuItem.roles.includes(this.currentRole);
        const isNewRole = this.$store.getters['getInboxPermissions'].length > 0;
        const routePermitted = this.routePermissions.includes(
          menuItem.toStateName
        );
        // admin and agent roles can't have permissions
        if (isNewRole && !routePermitted && menuItem.toStateName !== 'home') {
          return false;
        }
        if (!isNewRole && !isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const routeName = this.$route.name;
      if (routeName && routeName.startsWith('settings_integrations')) {
        const integrationsMenu = this.primaryMenuItems.find(item => item.key === 'integrations');
        if (integrationsMenu) return integrationsMenu;
      }

      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
    hasSecondaryMenu() {
      return !!(this.activeSecondaryMenu.menuItems && this.activeSecondaryMenu.menuItems.length);
    },
    showExportWizard() {
      return this.activePrimaryMenu.key === 'settings' && (this.isAdmin || this.hasCreatePermission('Export Wizard'));
    },
    showImportWizard() {
      return this.activePrimaryMenu.key === 'settings' && (this.isAdmin || this.hasCreatePermission('Import Wizard'));
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
  },

  methods: {
    togglePrimaryMenuWithTitles() {
      this.$store.commit('uiSettings/SET_PRIMARY_MENU_WITH_TITLES', !this.primaryMenuWithTitles)
    },
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleKeyShortcutModal() {
      this.$emit('open-key-shortcut-modal');
    },
    closeKeyShortcutModal() {
      this.$emit('close-key-shortcut-modal');
    },
    handleKeyEvents(e) {
      if (hasPressedCommandAndForwardSlash(e)) {
        this.toggleKeyShortcutModal();
      }
      if (isEscape(e)) {
        this.closeKeyShortcutModal();
      }

      if (hasPressedAltAndCKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('home')) {
          router.push({ name: 'home' });
        }
      } else if (hasPressedAltAndVKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('contacts_dashboard')) {
          router.push({ name: 'contacts_dashboard' });
        }
      } else if (hasPressedAltAndRKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('settings_account_reports')) {
          router.push({ name: 'settings_account_reports' });
        }
      } else if (hasPressedAltAndSKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('agent_list')) {
          router.push({ name: 'agent_list' });
        }
      }
    },
    isCurrentRouteSameAsNavigation(routeName) {
      return this.$route.name === routeName;
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggle-account-modal');
    },
    showAddLabelPopup() {
      this.$emit('show-add-label-popup');
    },
    openNotificationPanel() {
      this.$emit('open-notification-panel');
    },
  },
};
</script>

<style lang="scss" scoped>
.woot-sidebar {
  display: flex;
  min-height: 0;
  height: 100%;
  width: fit-content;
}
</style>

<style lang="scss">
@import '~dashboard/assets/scss/variables';

// d99d
.sidebar-wrapper {
  //border-right: 1px solid var(--s-50);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toggle--menu-type {
  height: 4rem;
  margin-inline-start: 4px;
  padding-top: 1.2rem;
  margin-top: -2rem;
  margin-bottom: -2rem;
  transform: scale(1, 0.4);
  opacity: 0.6;
  z-index: 18;

  &:hover {
    transform: scale(1, 0.6);
    opacity: 1;
  }

}
//.

.account-selector--modal {
  .modal-container {
    width: 40rem;
  }
}

.secondary-sidebar {
  overflow-y: auto;
  height: 100%;
}

.account-selector {
  cursor: pointer;
  padding: $space-small $space-large;

  .selected--account {
    margin-top: -$space-smaller;

    & + .account--details {
      padding-left: $space-normal - $space-micro;
    }
  }

  .account--details {
    padding-left: $space-large + $space-smaller;
  }

  &:last-child {
    margin-bottom: $space-large;
  }

  a {
    align-items: center;
    cursor: pointer;
    display: flex;

    .account--name {
      cursor: pointer;
      font-size: $font-size-medium;
      font-weight: $font-weight-medium;
      line-height: 1;
    }

    .account--role {
      cursor: pointer;
      font-size: $font-size-mini;
      text-transform: capitalize;
    }
  }
}

.app-context-menu {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 6rem;
}

.current-user--options {
  font-size: $font-size-big;
  margin-bottom: auto;
  margin-left: auto;
  margin-top: auto;
}

.secondary-menu .nested.vertical.menu {
  margin-left: var(--space-small);
}
</style>
